// src/pages/AnalysisSection.jsx
//
// Student-facing render of a skills-workshop `analysis` lit_section (examiner-insight
// / past-paper analysis). Mirrors TechniqueSection (pure presentational, takes
// `content` directly, neutral book-* palette) so it drops into the shared grouped
// reader the same way.
//
// Reads content.blocks: { whatExaminersWant, commonPitfalls:[...], modelApproach:[...],
// workedExample:[{prompt,model}] } (ANALYSIS_BLOCKS in litSectionShape.js). The unit
// grouping (blocks.unit) is nav metadata and is NOT rendered here.
import React from 'react';
import RichText from '../components/richText/RichText.jsx';
import WorkedExampleReveal from '../components/WorkedExampleReveal.jsx';
import { parseYouTubeId } from '../components/YouTubeEmbed.jsx';
import { KeyIdeaBox, TechniquePart } from './TechniqueSection.jsx';
import { practiceView } from '../components/practiceActivities/model.js';
import { selectRenderableParts } from './techniqueParts.js';
import LessonCard, { LessonCardHeading, ReadMore } from '../components/reader/LessonCard.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

export default function AnalysisSection({ content, done = false, lessonOptions }) {
    const blocks = (content && content.blocks) || {};
    const keyIdea = typeof blocks.keyIdea === 'string' ? blocks.keyIdea : '';
    // NEW model (analysis-vocab-flexible-subheadings): the author's flexible
    // subheadings. Non-empty -> they REPLACE the fixed "What examiners want" prose;
    // the structured blocks (pitfalls / approach / worked example) still follow.
    const parts = selectRenderableParts(blocks, parseYouTubeId);
    const practice = practiceView(content);
    // LEGACY model (never-migrated sections): the fixed whatExaminersWant prose.
    const whatExaminersWant = typeof blocks.whatExaminersWant === 'string' ? blocks.whatExaminersWant : '';
    const commonPitfalls = Array.isArray(blocks.commonPitfalls) ? blocks.commonPitfalls.filter((s) => typeof s === 'string' && s.trim()) : [];
    const modelApproach = Array.isArray(blocks.modelApproach) ? blocks.modelApproach.filter((s) => typeof s === 'string' && s.trim()) : [];
    const workedExample = Array.isArray(blocks.workedExample)
        ? blocks.workedExample.filter((e) => e && (e.prompt || e.model)) : [];
    const empty = !keyIdea && parts.length === 0 && !whatExaminersWant
        && !commonPitfalls.length && !modelApproach.length && !workedExample.length;

    // max-w-2xl caps the reading measure (~66-70ch at this serif size); heading,
    // prose, lists and worked-example cards all share the one capped column.
    return (
        <div className="max-w-2xl mx-auto pb-8">
            <LessonHeader
                eyebrow={typeof blocks.unit === 'string' && blocks.unit.trim() ? blocks.unit.trim() : 'Analysis'}
                title={(content && content.title) || 'Analysis'} blocks={blocks} done={done} />

            {empty && <EmptyNote />}

            <KeyIdeaBox keyIdea={keyIdea} />

            {parts.length > 0 ? (
                parts.map((p, i) => <TechniquePart key={(typeof p.id === 'string' && p.id) || 'part' + i} part={p} practice={practice} missionContent={content} lessonOptions={lessonOptions} />)
            ) : (whatExaminersWant && (
                <LessonCard className="mb-4">
                    <LessonCardHeading>What examiners want</LessonCardHeading>
                    <ReadMore text={whatExaminersWant}>
                        <RichText text={whatExaminersWant} className="font-serif text-book-text text-base leading-relaxed" />
                    </ReadMore>
                </LessonCard>
            ))}

            {commonPitfalls.length > 0 && (
                <div className="mb-4">
                    <LessonCardHeading>Common pitfalls</LessonCardHeading>
                    <ul className="space-y-2">
                        {commonPitfalls.map((s, i) => (
                            <LessonCard as="li" key={i} className="font-serif text-book-text leading-relaxed flex gap-3">
                                <span className="font-display text-book-accent flex-shrink-0 font-bold">&bull;</span>
                                <RichText text={s} />
                            </LessonCard>
                        ))}
                    </ul>
                </div>
            )}

            {modelApproach.length > 0 && (
                <div className="mb-4">
                    <LessonCardHeading>Model approach</LessonCardHeading>
                    <ol className="space-y-2">
                        {modelApproach.map((s, i) => (
                            <LessonCard as="li" key={i} className="font-serif text-book-text leading-relaxed flex gap-3">
                                <span className="font-display text-book-accent flex-shrink-0 font-bold">{i + 1}.</span>
                                <RichText text={s} />
                            </LessonCard>
                        ))}
                    </ol>
                </div>
            )}

            {workedExample.length > 0 && (
                <div className="mb-4">
                    <LessonCardHeading>Worked example</LessonCardHeading>
                    <div className="space-y-3">
                        {workedExample.map((e, i) => (
                            <WorkedExampleReveal key={i} prompt={e.prompt} model={e.model} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
